import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

import videoService from '../services/videoService';
import './Dashboard.css';

const generateMonthOptions = () => {
    const options = [];
    const now = new Date();
    // Generate last 6 months
    for (let i = 0; i < 6; i++) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        options.push(date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }));
    }
    return options;
};

const Dashboard = () => {
    const { isAdmin } = useAuth();

    const [stats, setStats] = useState({
        totalVideos: 0,
        todayVideos: 0,
        completedVideos: 0,
        failedVideos: 0
    });
    const [recentVideos, setRecentVideos] = useState([]);
    const [loading, setLoading] = useState(true);

    const monthOptions = generateMonthOptions();
    const [selectedMonth, setSelectedMonth] = useState(monthOptions[0]);

    useEffect(() => {
        if (selectedMonth) {
            fetchDashboardData();
        }
    }, [selectedMonth]);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const today = new Date().toISOString().split('T')[0];

            // Calculate date range for the selected month
            // selectedMonth is in "MMM YYYY" format (e.g., "Feb 2026")
            const [monthName, year] = selectedMonth.split(' ');
            const monthMap = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 };
            const monthIndex = monthMap[monthName];

            const startDate = new Date(parseInt(year), monthIndex, 1).toISOString().split('T')[0];
            const endDate = new Date(parseInt(year), monthIndex + 1, 0).toISOString().split('T')[0];

            // Fetch videos for stats in the selected month
            // We fetch with a larger limit to get more accurate status breakdown if needed,
            // but the total count comes from pagination.total
            const fetchFn = isAdmin() ? videoService.getAllVideos : videoService.getVideos;
            const videosResult = await fetchFn({
                startDate,
                endDate,
                limit: 1000
            });

            if (videosResult.success) {
                const videos = videosResult.videos;

                // Today's videos (if current month is selected)
                const todayVideos = videos.filter(v =>
                    v.createdAt.split('T')[0] === today
                );

                setStats({
                    totalVideos: videosResult.pagination.total,
                    todayVideos: todayVideos.length,
                    completedVideos: videos.filter(v => v.status === 'completed').length,
                    failedVideos: videos.filter(v => v.status === 'failed').length
                });

                setRecentVideos(videos.slice(0, 5));
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="dashboard-loading">
                <div className="loading-spinner"></div>
                <p>Loading dashboard...</p>
            </div>
        );
    }

    return (
        <div className="dashboard">
            <header className="dashboard-header">
                <div className="dashboard-title-area">
                    <h1>Dashboard</h1>
                    <p>Welcome back! Here's an overview of your video management system.</p>
                </div>
                <div className="dashboard-actions">
                    <select
                        className="date-select"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                    >
                        {monthOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </div>
            </header>

            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-main">
                        <span className="stat-label">Total Videos</span>
                        <h2 className="stat-value">{stats.totalVideos}</h2>
                    </div>
                    <div className="stat-icon-wrapper primary">
                        <i className="fas fa-video"></i>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-main">
                        <span className="stat-label">Today's Videos</span>
                        <h2 className="stat-value">{stats.todayVideos}</h2>
                    </div>
                    <div className="stat-icon-wrapper secondary">
                        <i className="fas fa-calendar-day"></i>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-main">
                        <span className="stat-label">Completed</span>
                        <h2 className="stat-value">{stats.completedVideos}</h2>
                    </div>
                    <div className="stat-icon-wrapper success">
                        <i className="fas fa-check-circle"></i>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-main">
                        <span className="stat-label">Failed</span>
                        <h2 className="stat-value">{stats.failedVideos}</h2>
                    </div>
                    <div className="stat-icon-wrapper danger">
                        <i className="fas fa-exclamation-circle"></i>
                    </div>
                </div>
            </div>

            <div className="dashboard-grid">
                <section className="dashboard-section recent-activity">
                    <div className="section-header">
                        <h2 className="section-title">Recent Videos</h2>
                        <Link to="/feeds" className="view-all-link">View All</Link>
                    </div>

                    {recentVideos.length > 0 ? (
                        <table className="mini-table">
                            <thead>
                                <tr>
                                    <th>AWB Number</th>
                                    <th>Scan Type</th>
                                    <th>Status</th>
                                    <th>Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentVideos.map(video => (
                                    <tr key={video._id}>
                                        <td>
                                            <Link to={`/feeds?awb=${video.awb}`} className="awb-link">
                                                {video.awb}
                                            </Link>
                                        </td>
                                        <td>
                                            <span className={`type-badge ${video.scanType}`}>
                                                {video.scanType}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${video.status}`}>
                                                {video.status}
                                            </span>
                                        </td>
                                        <td>{new Date(video.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="empty-state">
                            <i className="fas fa-video-slash" style={{ fontSize: '48px', opacity: 0.2, marginBottom: '16px' }}></i>
                            <p>No videos recorded yet</p>
                        </div>
                    )}
                </section>

                <section className="dashboard-section quick-actions-panel">
                    <div className="section-header">
                        <h2 className="section-title">Quick Actions</h2>
                    </div>
                    <div className="quick-actions">
                        <Link to="/recording" className="action-btn">
                            <span className="action-icon"><i className="fas fa-video"></i></span>
                            <span>Record Video</span>
                        </Link>
                        <Link to="/feeds" className="action-btn">
                            <span className="action-icon"><i className="fas fa-list"></i></span>
                            <span>View Feeds</span>
                        </Link>
                        <Link to="/uploading" className="action-btn">
                            <span className="action-icon"><i className="fas fa-upload"></i></span>
                            <span>Active Uploads</span>
                        </Link>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default Dashboard;
